/* Date-range helpers for the list filters.
 *
 * `new Date().toISOString()` reports the UTC calendar date. Dhaka is UTC+6, so
 * between midnight and 6am local it returns YESTERDAY - which made the default
 * range end a day early and hid the orders placed that night. These helpers
 * keep the <input type="date"> values on the LOCAL calendar day throughout. */

const pad = (value) => String(value).padStart(2, "0");

/* Date -> "YYYY-MM-DD" for an <input type="date">, in local time. */
export const toDateInputValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/* "YYYY-MM-DD" -> Date at local midnight. `new Date("2026-08-25")` parses as
 * UTC midnight, which lands on the wrong local day west of Greenwich. */
export const fromDateInputValue = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);
  if (!year || !month || !day) return new Date(value);
  return new Date(year, month - 1, day);
};

/* The default filter window: the last 30 days up to and including today. */
export const getDefaultDateRange = (days = 30) => {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - days);
  return {
    startDate: toDateInputValue(past),
    endDate: toDateInputValue(today),
  };
};

/* Records store the booked day as dd/mm/yyyy alongside a `time` millisecond
   stamp. `time` is authoritative; this is the fallback for older rows that
   only carry the printed date. */
export const parseDayMonthYear = (value) => {
  const [d, m, y] = String(value || "")
    .split("/")
    .map(Number);
  if (!d || !m || !y) return NaN;
  return new Date(y, m - 1, d).getTime();
};

/* Milliseconds for a record, or NaN when it carries no usable date. */
export const getRecordTime = (record) => {
  const stamp = parseInt(record && record.time, 10);
  if (Number.isFinite(stamp)) return stamp;
  return parseDayMonthYear(record && record.date);
};

/* Inclusive [start, end] test against "YYYY-MM-DD" input values. An empty
   bound is treated as open-ended. */
export const isWithinDateRange = (record, startValue, endValue) => {
  if (!startValue && !endValue) return true;
  const ms = getRecordTime(record);
  if (!Number.isFinite(ms)) return false;
  if (startValue) {
    const start = fromDateInputValue(startValue);
    start.setHours(0, 0, 0, 0);
    if (ms < start.getTime()) return false;
  }
  if (endValue) {
    const end = fromDateInputValue(endValue);
    end.setHours(23, 59, 59, 999);
    if (ms > end.getTime()) return false;
  }
  return true;
};
